import { setError, setLoading, setSession } from '@/store/auth/slice';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { UserRole } from '@/types/user';
import { isSuperAdminPath } from '@/ui/@components/utility/super-admin-access';
import { APP_ROUTES } from '@/ui/application-routes';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { authService } from './auth-service';

// Helper function to get the appropriate dashboard URL based on user role
const getDashboardUrl = (userRole?: UserRole) => {
  switch (userRole) {
    case UserRole.CHANNEL_PARTNER_ADMIN:
      return APP_ROUTES.PARTNER.DASHBOARD;
    case UserRole.COMPANY_ADMIN:
      return APP_ROUTES.DASHBOARD; // Company admins use the regular dashboard
    case UserRole.COMPANY_MANAGER:
      return APP_ROUTES.TEAM.DASHBOARD;
    case UserRole.COMPANY_MEMBER:
      return APP_ROUTES.DASHBOARD;
    default:
      return APP_ROUTES.DASHBOARD;
  }
};

/**
 * useAuthSetup hook fetches the session and sets the session state in the store. It also handles the auth state change and redirects the user to the appropriate route based on the auth state.
 */
export const useAuthSetup = () => {
  const session = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const isDesktop = (() => {
    try {
      const params = new URLSearchParams(search);
      return (params.get('desktop') || '').toLowerCase() === 'true';
    } catch {
      return false;
    }
  })();
  const isReauth = (() => {
    try {
      const params = new URLSearchParams(search);
      return (params.get('reauth') || '').toLowerCase() === 'true';
    } catch {
      return false;
    }
  })();
  const isPublicSession = (() => {
    try {
      const params = new URLSearchParams(search);
      return (
        (pathname.startsWith('/sessions/') &&
          params.get('public') === 'true') ||
        (pathname.startsWith('/meetings/') && params.get('public') === 'true')
      );
    } catch {
      return false;
    }
  })();

  const handleFetchSession = async () => {
    await authService.handleOAuthCallback?.();
    const { data, error } = await authService.getSession();
    if (!data.session) {
      // Let auth, public reports, and platform-admin routes handle access themselves.
      if (
        !pathname.startsWith(APP_ROUTES.AUTH.MAIN) &&
        !isPublicSession &&
        !isSuperAdminPath(pathname)
      )
        navigate(APP_ROUTES.AUTH.MAIN, { replace: true });
    } else if (
      // Skip auto-redirects on auth routes during desktop popup or reauth flows
      !(pathname.startsWith(APP_ROUTES.AUTH.MAIN) && (isDesktop || isReauth))
    ) {
      if (
        data.session.user.userMetadata?.needs_password_set &&
        !pathname.startsWith(APP_ROUTES.AUTH.SET_PASSWORD)
      ) {
        navigate(APP_ROUTES.AUTH.SET_PASSWORD, { replace: true });
      }
    }

    dispatch(setSession(data.session));
    dispatch(setError(error));
    dispatch(setLoading(false));
  };

  useEffect(() => {
    handleFetchSession();

    const {
      data: { subscription },
    } = authService.onAuthStateChange((_event, session) => {
      // Skip auth redirects for public session routes
      if (isPublicSession) {
        dispatch(setSession(session));
        return;
      }
      if (session && !session.user.emailConfirmedAt) {
        // Skip redirects while on auth routes during desktop/reauth flows
        if (
          !(
            pathname.startsWith(APP_ROUTES.AUTH.MAIN) &&
            (isDesktop || isReauth)
          ) &&
          !pathname.startsWith(APP_ROUTES.AUTH.VERIFY_EMAIL)
        )
          navigate(APP_ROUTES.AUTH.VERIFY_EMAIL, { replace: true });
        return;
      }
      dispatch(setSession(session));
      switch (_event) {
        case 'SIGNED_IN':
          // Skip all redirects while on auth routes during desktop popup or reauth flows
          if (
            pathname.startsWith(APP_ROUTES.AUTH.MAIN) &&
            (isDesktop || isReauth)
          ) {
            break;
          }
          if (
            !session?.user?.emailConfirmedAt &&
            !pathname.startsWith(APP_ROUTES.AUTH.VERIFY_EMAIL)
          )
            navigate(APP_ROUTES.AUTH.VERIFY_EMAIL, { replace: true });
          else if (
            session?.user?.userMetadata?.needs_password_set &&
            !pathname.startsWith(APP_ROUTES.AUTH.SET_PASSWORD)
          )
            navigate(APP_ROUTES.AUTH.SET_PASSWORD, { replace: true });
          else if (
            session?.user?.emailConfirmedAt &&
            pathname.startsWith(APP_ROUTES.AUTH.MAIN)
          ) {
            // Redirect to appropriate dashboard based on user role
            const userRole = session.user.userMetadata?.role as UserRole;
            const dashboardUrl = getDashboardUrl(userRole);
            navigate(dashboardUrl, { replace: true });
          }
          break;
        case 'REMOTE_SIGNED_OUT':
        case 'ACCOUNT_SWITCHED':
          // Another tab signed out or signed in a different account. Skip
          // desktop popup and reauth flows, which hand off tokens themselves.
          if (
            pathname.startsWith(APP_ROUTES.AUTH.MAIN) &&
            (isDesktop || isReauth)
          ) {
            break;
          }
          // Full page load so no state or cached API data from the previous
          // account survives; the root route restores the current account.
          window.location.replace(
            _event === 'ACCOUNT_SWITCHED'
              ? APP_ROUTES.ROOT
              : APP_ROUTES.AUTH.LOGIN
          );
          break;
      }
    });

    return () => subscription.unsubscribe();
    // eslint-disable-next-line
  }, [pathname, search]);

  return { session };
};
